// src/components/Settings/Pages/UserManagement.tsx
import React from "react";
import { Box, Typography, LinearProgress, Alert } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import { useUserManagement } from "../../../hooks/useUserManagement";

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const { users, loading, error, formatDate, formatNumber } =
    useUserManagement();
  const paginationModel = { page: 0, pageSize: 10 };

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "ชื่อ",
      width: 320,
      flex: 1,
    },
    {
      field: "email",
      headerName: "Email",
      width: 320,
      flex: 1,
    },
    {
      field: "tokenUsage",
      headerName: "Token ที่ใช้ไป",
      width: 160,
      renderCell: (params) => formatNumber(params.value),
      align: "right",
      headerAlign: "right",
    },
    {
      field: "tokenLimit",
      headerName: "Token ที่จำกัด",
      width: 160,
      renderCell: (params) => formatNumber(params.value),
      align: "right",
      headerAlign: "right",
    },
    {
      field: "lastActive",
      headerName: "ใช้งานล่าสุด",
      width: 240,
      renderCell: (params) => formatDate(params.value),
    },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "column", md: "row" },
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          mb: 4,
          gap: 2,
        }}
      >
        <AdminPanelSettingsOutlinedIcon color="warning" sx={{ fontSize: 32 }} />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            textAlign: { xs: "center", sm: "center", md: "left" },
          }}
        >
          <Typography
            variant="body1"
            color="textPrimary"
            sx={{ fontWeight: 600 }}
          >
            ข้อมูลผู้ใช้งาน
          </Typography>
          <Typography variant="body2" color="textSecondary">
            รายการผู้ใช้งาน และข้อมูลต่างๆ
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            p: 4,
            gap: 2,
          }}
        >
          <TableViewOutlinedIcon
            sx={{ fontSize: 24, color: theme.palette.text.secondary }}
          />
          <LinearProgress
            color="primary"
            sx={{ width: "200px", borderRadius: "4px" }}
          />
        </Box>
      )}

      {!loading && !error && users.length > 0 && (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <DataGrid
            rows={users}
            columns={columns}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
          />
        </Box>
      )}

      {!loading && !error && users.length === 0 && (
        <Alert severity="info" sx={{ width: "100%", mb: 2 }}>
          ไม่พบข้อมูลผู้ใช้งาน
        </Alert>
      )}
    </Box>
  );
};

export default UserManagement;
